import { IResource } from "@gettactic/api";
import { IconValue } from "@gettactic/components";
import { LucideIcon } from "lucide-react";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { Furniture } from "../../../../furniture-list";
import {
  CircleShape,
  DrawShape,
  DrawToolState,
  GroupShape,
  ResourceEditingOptions,
  Shape,
  SpotOrientation,
  SpotShape,
  WallPathShape,
  WallShape,
  ZoneShape,
  ZoneTypeDefinition
} from "../../../../types";
import { getRandomColor } from "../../../../utils/colors";
import { generateShapeId } from "../../../../utils/generateShapeId";
import {
  createCircle,
  createImageElement,
  createNodeShape,
  createRectangle,
  createSvgElement,
  createText,
  createZone
} from "../../../../utils/shapeFactory";
import { loadSvgFile } from "../../../../utils/svg";
import {
  calculateGroupBounds,
  flattenGroup
} from "../../../../utils/transform";
import {
  readShapesFromSystemClipboard,
  writeShapesToSystemClipboard
} from "../../editor/helpers/clipboardSerialize";
import { HistoryManager } from "../../editor/helpers/history";
import { generateNextSpotLabels } from "../../helpers/generateNextSpotLabels";
import { getElementSize } from "../../helpers/getElementSize";
import { getWindowKonvaObject } from "../../helpers/konva/getWindowKonvaObject";
import { transformCompanions } from "../../helpers/transformCompanions";
import {
  MapEditorTool,
  PreviewShape,
  ShapeEditingHighlightOptions
} from "../../types";
import {
  MapCanvasValue,
  useMapCanvas,
  useMapCanvasStage
} from "../MapCanvasContext";
import type {
  MapSaveMode,
  MapSaveOptions,
  MapSaveResult
} from "../MapCanvasContext/MapCanvasContext.types";
import {
  AddImageFromFileOptions,
  AddSVGFromFileOptions,
  ClipboardPasteOptions,
  DuplicateShapeOptions,
  MapEditorActionsState,
  MapEditorClipboard,
  MapEditorPasteEvent,
  MapEditorPropsState,
  MapEditorSelectionState,
  MapEditorState,
  MapEditorToolState,
  MapEditorUIState,
  ZoneEditorProps
} from "./MapEditorContext.types";
import { MapEditorActionsContext } from "./useMapEditorActions";
import { MapEditorPropsContext } from "./useMapEditorProps";
import { MapEditorSelectionContext } from "./useMapEditorSelection";
import { MapEditorToolContext } from "./useMapEditorTool";
import { MapEditorUIContext } from "./useMapEditorUI";

/** Tools that make zone regions; using one shows the zones. */
const isZoneTool = (tool: MapEditorTool): boolean =>
  tool === "zone" || tool === "auto-zone" || tool === "zone-polygon";

export interface MapEditorProviderProps {
  children: React.ReactNode;
  resources?: IResource[];
  setEditingResources: (value: ResourceEditingOptions) => void;
  editingResources?: ResourceEditingOptions;
  editorRef?: React.RefObject<MapEditorState | null | undefined>;
  onSave?: (
    data: MapCanvasValue,
    saveOptions: MapSaveOptions
  ) => MapSaveResult | Promise<MapSaveResult>;
  onExit?: () => void;
  title?: string;
  titlePrefix?: string;
  headerSlot?: React.ReactNode;
  zoneEditor?: React.ComponentType<ZoneEditorProps>;
  restrictedMode?: boolean;
  saveModes?: MapSaveMode[];
  /** Tool the canvas starts on, instead of the default "select". */
  initialActiveTool?: MapEditorTool;
  /** Start on the zone tool with this organisation zone as the tag, so the
   *  first drag draws a region for it (Settings → "Draw on a floor"). */
  initialZoneToolTag?: ZoneTypeDefinition | null;
  initialShowZones?: boolean;
  /** A read-only reference point rendered on the canvas — see
   *  {@link MapEditorPropsState.guidePoint}. */
  guidePoint?: { x: number; y: number };
}

export const MapEditorProvider: React.FC<MapEditorProviderProps> = ({
  children,
  resources,
  setEditingResources,
  editingResources,
  editorRef,
  onSave,
  onExit,
  title,
  titlePrefix,
  headerSlot,
  zoneEditor,
  restrictedMode,
  saveModes,
  initialActiveTool,
  initialZoneToolTag,
  initialShowZones,
  guidePoint
}) => {
  const mapCanvas = useMapCanvas();
  const { stagePositionRef, setStagePosition } = useMapCanvasStage();

  const {
    shapesRef,
    setShapes: setMapShapes,
    gridSize,
    getCanvasValue
  } = mapCanvas;
  const shouldPreventDeselect = React.useRef(false);
  const [selectedIds, _setSelectedIds] = React.useState<string[]>([]);
  const selectedIdsRef = React.useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const [showOutside, setShowOutside] = React.useState(true);
  const [snapToGrid, setSnapToGrid] = React.useState(true);
  const [showSnapLines, setShowSnapLines] = React.useState(true);
  const [showGrid, setShowGrid] = React.useState(true);
  const startTool: MapEditorTool =
    initialActiveTool ?? (initialZoneToolTag ? "zone" : "select");
  const [activeTool, setActiveTool] = React.useState<MapEditorTool>(startTool);
  const [clipboard, setClipboard] = React.useState<Shape[]>([]);
  const [nodeStyleToolTarget, setNodeStyleToolTarget] =
    React.useState<SpotShape | null>(null);
  const [wallPathSelectedVertexIndex, setWallPathSelectedVertexIndex] =
    React.useState<number | null>(null);

  const [previewShape, setPreviewShape] = React.useState<PreviewShape | null>(
    null
  );

  const [iconToolSelectedIcon, setIconToolSelectedIcon] =
    React.useState<IconValue | null>(null);
  const [furnitureToolSelectedFurniture, setFurnitureToolSelectedFurniture] =
    React.useState<Furniture | null>(null);
  const [showZoneChecks, setShowZoneChecks] = React.useState(true);
  // Off while building; a zone tool, picked or opened on (Settings' "Draw
  // on a floor"), switches them on, as does a host that asks for them.
  const [showZones, setShowZones] = React.useState(
    () => !!initialShowZones || isZoneTool(startTool)
  );
  const setActiveToolShowingZones = React.useCallback((tool: MapEditorTool) => {
    if (isZoneTool(tool)) setShowZones(true);
    setActiveTool(tool);
  }, []);
  const [zoneToolTag, setZoneToolTagState] =
    React.useState<ZoneTypeDefinition | null>(initialZoneToolTag ?? null);
  const zoneToolTagRef = React.useRef<ZoneTypeDefinition | null>(
    initialZoneToolTag ?? null
  );
  const setZoneToolTag = React.useCallback((tag: ZoneTypeDefinition | null) => {
    zoneToolTagRef.current = tag;
    setZoneToolTagState(tag);
  }, []);

  const [contextMenuShape, setContextMenuShape] = React.useState<Shape | null>(
    null
  );
  const [canUndo, setCanUndo] = React.useState(false);
  const [canRedo, setCanRedo] = React.useState(false);

  const [drawTool, setDrawTool] = React.useState<DrawToolState>({
    active: false,
    selectedShape: "rectangle",
    startPoint: null
  });

  const [highlightedShape, setHighlightedShape] =
    React.useState<ShapeEditingHighlightOptions | null>(null);

  const historyManager = React.useRef(
    new HistoryManager({ shapes: [], selectedIds: [] })
  );

  // Refs to stabilize callbacks that depend on changing props/state
  const clipboardRef = React.useRef(clipboard);
  clipboardRef.current = clipboard;

  const onSaveRef = React.useRef(onSave);
  onSaveRef.current = onSave;

  const getCanvasValueRef = React.useRef(getCanvasValue);
  getCanvasValueRef.current = getCanvasValue;

  const setEditingResourcesRef = React.useRef(setEditingResources);
  setEditingResourcesRef.current = setEditingResources;

  const editingResourcesRef = React.useRef(editingResources);
  editingResourcesRef.current = editingResources;

  const updateHistory = React.useCallback(
    (shapes: Shape[], selectedIds: string[]) => {
      historyManager.current.pushState({ shapes, selectedIds });
      setCanUndo(historyManager.current.canUndo());
      setCanRedo(historyManager.current.canRedo());
    },
    []
  );

  const setSelectedIds = React.useCallback(
    (ids: string[] | ((prev: string[]) => string[])) => {
      if (!editingResourcesRef.current?.isOpen) {
        return _setSelectedIds(ids);
      }

      // Walk the selection in selection order (not shapesRef order) so the
      // Resource Editor panel renders entries in the order the user picked
      // them. Filtering shapesRef directly would reorder by canvas/creation
      // order, which is what made bulk-edit numbering get out of sync with
      // the order the user expected.
      const buildResourceIds = (selectedIds: string[]): string[] => {
        const shapeById = new Map<string, Shape>();
        for (const shape of shapesRef.current) shapeById.set(shape.id, shape);
        const result: string[] = [];
        for (const id of selectedIds) {
          const shape = shapeById.get(id) as SpotShape | undefined;
          if (shape?.type === "spot" && shape.resource_id) {
            result.push(shape.resource_id);
          }
        }
        return result;
      };

      if (typeof ids === "function") {
        _setSelectedIds((prev) => {
          const newIds = ids(prev);
          setEditingResourcesRef.current({
            isOpen: true,
            resourceIds: buildResourceIds(newIds)
          });
          return newIds;
        });
      } else {
        setEditingResourcesRef.current({
          isOpen: true,
          resourceIds: buildResourceIds(ids)
        });
        _setSelectedIds(ids);
      }
    },
    []
  );

  // Tracks whether the user has changed anything since the last save / load.
  // Mutations that go through history (allowHistoryUpdate=true) flip it on;
  // a successful save flips it off. The ref-mirror lets the beforeunload and
  // popstate handlers — attached once on mount — read the live value.
  const [isDirty, setIsDirtyState] = React.useState(false);
  const isDirtyRef = React.useRef(false);
  const markDirty = React.useCallback((value: boolean) => {
    if (isDirtyRef.current === value) return;
    isDirtyRef.current = value;
    setIsDirtyState(value);
  }, []);

  const setShapes: (
    action: React.SetStateAction<Shape[]>,
    allowHistoryUpdate?: boolean
  ) => void = React.useCallback(
    (action, allowHistoryUpdate = true) => {
      // Guard against StrictMode calling the updater twice:
      // both calls share this closure variable, so the second call skips.
      let historyPushed = false;
      setMapShapes((prevShapes) => {
        const newShapes =
          typeof action === "function" ? action(prevShapes) : action;
        if (allowHistoryUpdate && !historyPushed) {
          historyPushed = true;
          // Sync base state with loaded shapes before the first push
          historyManager.current.ensureBaseState(
            prevShapes,
            selectedIdsRef.current
          );
          updateHistory(newShapes, selectedIdsRef.current);
          // Any user-driven shape mutation makes the canvas dirty. Loads use
          // allowHistoryUpdate=false and so don't flip the flag.
          markDirty(true);
        }
        return newShapes;
      });
    },
    [setMapShapes, updateHistory, markDirty]
  );

  const updateShape = React.useCallback(
    (id: string, newProps: Partial<Shape>, allowHistoryUpdate = true) => {
      const updateInGroup = (shapes: Shape[]): Shape[] => {
        return shapes.map((shape) => {
          if (shape.id === id) {
            const updated = { ...shape, ...newProps } as Shape;

            // Transform companion positions when spot orientation changes.
            // Skip for composite template spots — their companion layout is
            // fixed by the template and should not be mirrored.
            if (
              shape.type === "spot" &&
              (newProps as Partial<SpotShape>).orientation
            ) {
              const spot = shape as SpotShape;
              const newOrientation = (newProps as Partial<SpotShape>)
                .orientation!;
              if (
                spot.companions?.length &&
                newOrientation !== spot.orientation &&
                !spot.compositeTemplateId
              ) {
                (updated as SpotShape).companions = transformCompanions(
                  spot.companions,
                  spot.width,
                  spot.height,
                  spot.orientation,
                  newOrientation
                );
              }

              // Compensate shape position when flipping top↔bottom so the
              // visual center stays in place. The visual content is offset
              // differently within the shape group for each orientation
              // (top desk: y=0–90, bottom desk: y=10–100 within 100-tall group).
              // Skip for composites — companions don't move on flip, so the
              // visual bounds shift is not symmetric.
              if (
                !spot.compositeTemplateId &&
                newOrientation !== spot.orientation &&
                ((spot.orientation === "top" && newOrientation === "bottom") ||
                  (spot.orientation === "bottom" && newOrientation === "top"))
              ) {
                const stage = getWindowKonvaObject().stages[0];
                if (stage) {
                  const size = getElementSize(shape.id, stage);
                  if (size) {
                    const topPad = size.visualY - shape.y;
                    const bottomPad =
                      shape.y +
                      shape.height -
                      (size.visualY + size.visualHeight);
                    // Shift position so the visual range stays in place
                    updated.y = shape.y + (topPad - bottomPad);
                  }
                }
              }
            }

            return updated;
          }

          if (shape.type === "group" && Array.isArray(shape.children)) {
            return {
              ...shape,
              children: updateInGroup(shape.children)
            };
          }

          return shape;
        });
      };

      setShapes((prev) => updateInGroup(prev), allowHistoryUpdate);
    },
    [setShapes]
  );

  // Keyboard movement: move Konva nodes directly for instant visual feedback,
  // debounce React state commit until keypress stops (like mouse drag on release).
  const keyMoveCommitTimer = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const accumulatedKeyMove = React.useRef<{ dx: number; dy: number }>({
    dx: 0,
    dy: 0
  });

  const useShapeKeyboardMovement = React.useCallback(() => {
    // biome-ignore lint/correctness/useHookAtTopLevel: pre-existing pattern — the callback runs unconditionally during render (line ~471); untangling it is out of scope for the UI pass
    React.useEffect(() => {
      if (selectedIds.length === 0) return;

      const selectedIdSet = new Set(selectedIds);

      const commitMove = () => {
        keyMoveCommitTimer.current = null;
        const { dx, dy } = accumulatedKeyMove.current;
        if (dx === 0 && dy === 0) return;
        accumulatedKeyMove.current = { dx: 0, dy: 0 };

        setShapes((prev) => {
          return prev.map((shape) => {
            if (!selectedIdSet.has(shape.id)) return shape;

            let newX = shape.x + dx;
            let newY = shape.y + dy;

            if (snapToGrid) {
              newX = Math.round(newX / gridSize) * gridSize;
              newY = Math.round(newY / gridSize) * gridSize;
            }

            // Actual displacement after potential grid snap
            const actualDx = newX - shape.x;
            const actualDy = newY - shape.y;

            const updated = { ...shape, x: newX, y: newY } as Shape;

            if (shape.type === "wall") {
              const wall = shape as WallShape;
              (updated as WallShape).x2 = wall.x2 + actualDx;
              (updated as WallShape).y2 = wall.y2 + actualDy;
            }

            if (shape.type === "wall-path") {
              const path = shape as WallPathShape;
              (updated as WallPathShape).vertices = path.vertices.map((v) => ({
                ...v,
                x: v.x + actualDx,
                y: v.y + actualDy
              }));
            }

            return updated;
          });
        });
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        const activeElement = document.activeElement;

        if (
          activeElement &&
          ["INPUT", "TEXTAREA"].includes(activeElement.tagName)
        ) {
          return;
        }

        // CMD/CTRL + Arrow is handled by the spot duplication shortcut
        if (e.metaKey || e.ctrlKey) return;

        const minPixels = snapToGrid ? gridSize : 1;
        const MOVE_SPEED = e.shiftKey ? minPixels * 5 : minPixels;

        let dx = 0;
        let dy = 0;

        switch (e.key) {
          case "ArrowUp":
            dy = -MOVE_SPEED;
            break;
          case "ArrowDown":
            dy = MOVE_SPEED;
            break;
          case "ArrowLeft":
            dx = -MOVE_SPEED;
            break;
          case "ArrowRight":
            dx = MOVE_SPEED;
            break;
          default:
            return;
        }

        e.preventDefault();

        // Move Konva nodes directly for instant visual feedback
        const konva = getWindowKonvaObject();
        const stage = konva?.stages?.[0];
        if (stage) {
          for (const id of selectedIds) {
            const node = stage.findOne(`#shape-${id}`);
            if (node) {
              node.move({ x: dx, y: dy });
            }
          }
          stage.batchDraw();
        }

        // Accumulate total delta for the state commit
        accumulatedKeyMove.current.dx += dx;
        accumulatedKeyMove.current.dy += dy;

        // Debounce state commit: only update React state after 300ms of no keypresses
        if (keyMoveCommitTimer.current) {
          clearTimeout(keyMoveCommitTimer.current);
        }
        keyMoveCommitTimer.current = setTimeout(commitMove, 300);
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        // Commit any pending movement immediately on cleanup
        if (keyMoveCommitTimer.current) {
          clearTimeout(keyMoveCommitTimer.current);
          commitMove();
        }
      };
    }, [selectedIds, gridSize, snapToGrid, setShapes]);
  }, [selectedIds, gridSize, snapToGrid, setShapes]);

  useShapeKeyboardMovement();

  const undo = React.useCallback(() => {
    const previousState = historyManager.current.undo();
    if (previousState) {
      setShapes(previousState.shapes, false);
      setSelectedIds(previousState.selectedIds);
      setCanUndo(historyManager.current.canUndo());
      setCanRedo(historyManager.current.canRedo());
    }
  }, []);

  const redo = React.useCallback(() => {
    const nextState = historyManager.current.redo();
    if (nextState) {
      setShapes(nextState.shapes, false);
      setSelectedIds(nextState.selectedIds);
      setCanUndo(historyManager.current.canUndo());
      setCanRedo(historyManager.current.canRedo());
    }
  }, []);

  const addShape = React.useCallback(
    (position?: { x?: number; y?: number }) => {
      const newShape = createRectangle(position);
      setShapes((prev) => [...prev, newShape]);
    },
    []
  );

  const disableHistoryRecording = React.useCallback(() => {
    historyManager.current.disableRecording();
  }, []);

  const enableHistoryRecording = React.useCallback(() => {
    historyManager.current.enableRecording();
  }, []);

  const addSvg = React.useCallback((position?: { x?: number; y?: number }) => {
    const newShape = createSvgElement(position);
    setShapes((prev) => [...prev, newShape]);
  }, []);

  const addSvgFromFile = React.useCallback(
    async (file: string, options?: AddSVGFromFileOptions) => {
      const svgShape = options?.shape;
      try {
        const svgData = await loadSvgFile(file);
        const response = await fetch(svgData.blobUrl);
        const svgText = await response.text();
        // Pick the source dimensions: caller-provided when present (built-in
        // furniture from FURNITURE_LIST), otherwise the SVG's natural size.
        // Then clamp the result so the longest side never exceeds the cap —
        // this is essential for *uploaded* furniture, where width/height
        // often come from the SVG's raw viewBox (commonly 1000+ units) and
        // would land as a giant shape on the canvas. Built-in dimensions
        // are already small (~50–120 px) so the clamp is a no-op for them.
        const NATURAL_SIZE_CAP = 200;
        const naturalW = svgData.width || 0;
        const naturalH = svgData.height || 0;
        let sourceWidth = svgShape?.width ?? naturalW;
        let sourceHeight = svgShape?.height ?? naturalH;

        // If the caller supplied width/height that don't match the SVG's
        // natural aspect ratio, snap to the natural aspect using the
        // caller's longest side as the target. This is what catches
        // *uploaded* furniture: the Furniture API record sometimes carries
        // square (e.g. 100×100) thumbnail dimensions even when the
        // underlying SVG has a tall/wide aspect, and rendering with
        // preserveAspectRatio="none" then visibly stretches it.
        if (
          svgShape?.width &&
          svgShape?.height &&
          naturalW > 0 &&
          naturalH > 0
        ) {
          const naturalAspect = naturalW / naturalH;
          const callerAspect = sourceWidth / sourceHeight;
          if (Math.abs(callerAspect - naturalAspect) > 0.05) {
            const target = Math.max(sourceWidth, sourceHeight);
            if (naturalAspect >= 1) {
              sourceWidth = target;
              sourceHeight = target / naturalAspect;
            } else {
              sourceHeight = target;
              sourceWidth = target * naturalAspect;
            }
          }
        }

        const longest = Math.max(sourceWidth, sourceHeight);
        const clampScale =
          longest > NATURAL_SIZE_CAP ? NATURAL_SIZE_CAP / longest : 1;
        const finalWidth = sourceWidth * clampScale;
        const finalHeight = sourceHeight * clampScale;
        const newShape = createSvgElement(
          svgShape?.x || svgShape?.y
            ? { x: svgShape?.x, y: svgShape?.y }
            : undefined,
          options?.saveOriginalUrl ? file : svgData.blobUrl,
          options?.saveContent !== false ? svgText : undefined,
          finalWidth,
          finalHeight
        );

        newShape.layerType = svgShape?.layerType || "shapes";
        if (svgShape?.locked) newShape.locked = svgShape.locked;
        setShapes((prev) => [...prev, newShape]);

        return { shapes: [newShape], svg: { ...svgData } };
      } catch (_error) {}

      return undefined;
    },
    []
  );

  const addImageFromFile = React.useCallback(
    async (file: string, options?: AddImageFromFileOptions) => {
      const imageShape = options?.shape;
      try {
        const response = await fetch(file);
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);

        const image = new Image();
        const imageLoadPromise = new Promise<{ width: number; height: number }>(
          (resolve, reject) => {
            image.onload = () =>
              resolve({ width: image.width, height: image.height });
            image.onerror = reject;
          }
        );
        image.src = imageUrl;

        const { width, height } = await imageLoadPromise;

        const newShape = createImageElement(
          imageShape?.x || imageShape?.y
            ? { x: imageShape?.x, y: imageShape?.y }
            : undefined,
          options?.saveOriginalUrl ? file : imageUrl,
          width,
          height
        );
        newShape.layerType = imageShape?.layerType || "shapes";
        if (imageShape?.locked) newShape.locked = imageShape.locked;
        setShapes((prev) => [...prev, newShape]);

        return { shapes: [newShape], image: { width, height, blobUrl: "" } };
      } catch (_error) {}

      return undefined;
    },
    []
  );

  const addIconSvg = React.useCallback(
    (Icon: LucideIcon, position?: { x?: number; y?: number }) => {
      // Render Lucide at a high intrinsic size so the source SVG has
      // enough resolution when Konva's <Image> rasterizes and scales it
      // to the placed shape size. Keeping width="256" height="256"
      // attributes is important: that's what the browser uses as the
      // image's intrinsic size when rendering for canvas — strip them
      // and it falls back to the 24-unit viewBox and the icon ends up
      // blocky / contracted again.
      const svgString = ReactDOMServer.renderToString(<Icon size={256} />);

      const blob = new Blob([svgString], { type: "image/svg+xml" });
      const pathData = URL.createObjectURL(blob);

      // Placed icon defaults: a reasonable on-canvas size that sits well
      // alongside furniture (~50–120 px). svgContent is embedded too so
      // viewer paths that consult content (e.g., SVG view fallbacks)
      // don't need an extra fetch.
      const newShape = createSvgElement(position, pathData, svgString, 60, 60);
      setShapes((prev) => [...prev, newShape]);
    },
    []
  );

  const addCustomMultiShape = React.useCallback(
    (position?: { x?: number; y?: number }) => {
      const newShape = createNodeShape(position);
      setShapes((prev) => [...prev, newShape]);
    },
    []
  );

  const addText = React.useCallback((position?: { x?: number; y?: number }) => {
    const newShape = createText(position);
    setShapes((prev) => [...prev, newShape]);
  }, []);

  const updateShapes = React.useCallback(
    (ids: string[], propertiesToUpdate: Partial<Shape>) => {
      const updateInGroup = (shapes: Shape[]): Shape[] => {
        return shapes.map((shape) => {
          if (ids.includes(shape.id)) {
            return { ...shape, ...propertiesToUpdate } as Shape;
          }

          if (shape.type === "group" && Array.isArray(shape.children)) {
            return {
              ...shape,
              children: updateInGroup(shape.children)
            };
          }

          return shape;
        });
      };

      setShapes((prev) => updateInGroup(prev));
    },
    []
  );

  const deleteShape = React.useCallback((id: string | string[]) => {
    const deleteInGroup = (shapes: Shape[]): Shape[] => {
      return shapes
        .filter((shape) =>
          Array.isArray(id) ? !id.includes(shape.id) : shape.id !== id
        )
        .map((shape) =>
          shape.type === "group" && Array.isArray(shape.children)
            ? { ...shape, children: deleteInGroup(shape.children) }
            : shape
        );
    };

    setShapes((prev) => deleteInGroup(prev));

    setSelectedIds((prev) =>
      prev.filter((selectedId) =>
        Array.isArray(id) ? !id.includes(selectedId) : selectedId !== id
      )
    );
  }, []);

  const moveShape = React.useCallback(
    (id: string, direction: "up" | "down") => {
      setShapes((prev) => {
        // Helper function to find and move shape inside a group
        const moveInGroup = (shapes: Shape[]): Shape[] => {
          const index = shapes.findIndex((shape) => shape.id === id);
          if (index === -1) {
            return shapes.map((shape) =>
              shape.type === "group" && Array.isArray(shape.children)
                ? { ...shape, children: moveInGroup(shape.children) }
                : shape
            );
          }

          const newShapes = [...shapes];
          if (direction === "up" && index < shapes.length - 1) {
            [newShapes[index], newShapes[index + 1]] = [
              newShapes[index + 1],
              newShapes[index]
            ];
          } else if (direction === "down" && index > 0) {
            [newShapes[index], newShapes[index - 1]] = [
              newShapes[index - 1],
              newShapes[index]
            ];
          }
          return newShapes;
        };

        return moveInGroup(prev);
      });
    },
    []
  );

  const duplicateShape = React.useCallback(
    (id: string | string[], options?: DuplicateShapeOptions) => {
      const ids = Array.isArray(id) ? id : [id];

      const duplicateRecursive = (shape: Shape): Shape => {
        let override: Partial<Shape> | undefined;

        if (Array.isArray(options?.override)) {
          override = options.override.find(
            (o) => o.id === shape.id
          ) as Partial<Shape>;
        } else {
          override = options as Partial<Shape>;
        }

        const offsetX = options?.offset?.x ?? 20;
        const offsetY = options?.offset?.y ?? 20;

        const newShape = {
          ...shape,
          x: shape.x + offsetX,
          y: shape.y + offsetY,
          ...override,
          id: generateShapeId(shape.type)
        };

        if (shape.type === "wall") {
          const wall = shape as WallShape;
          (newShape as WallShape).x2 = wall.x2 + offsetX;
          (newShape as WallShape).y2 = wall.y2 + offsetY;
          return newShape as Shape;
        }

        if (shape.type === "wall-path") {
          const path = shape as WallPathShape;
          (newShape as WallPathShape).vertices = path.vertices.map((v) => ({
            ...v,
            x: v.x + offsetX,
            y: v.y + offsetY
          }));
          return newShape as Shape;
        }

        if (shape.type === "spot") {
          const spotShape = shape as SpotShape;
          const originalOrientation = spotShape.orientation;
          const newOrientation =
            (override as Partial<SpotShape>)?.orientation ||
            originalOrientation;

          let companions = spotShape.companions?.map((companion) => ({
            ...companion,
            id: generateShapeId(companion.type)
          }));

          // Transform companion positions when orientation changes.
          // Skip for composite template spots — their layout is fixed.
          if (
            companions?.length &&
            newOrientation !== originalOrientation &&
            !spotShape.compositeTemplateId
          ) {
            companions = transformCompanions(
              companions,
              spotShape.width,
              spotShape.height,
              originalOrientation,
              newOrientation
            );
          }

          return {
            ...newShape,
            resource_id:
              options?.switchResourceId && ids.length === 1
                ? shape.resource_id
                : undefined,
            companions
          } as Shape;
        }

        if (shape.type === "group" && Array.isArray(shape.children)) {
          return {
            ...newShape,
            children: shape.children.map(duplicateRecursive) // Recursively duplicate children
          } as Shape;
        }

        return newShape as Shape;
      };

      const selectedShapes = shapesRef.current.filter((shape) =>
        ids.includes(shape.id)
      );

      if (selectedShapes.length === 0) return;

      const newShapes = selectedShapes.map(duplicateRecursive);

      setShapes((prev) => {
        let updatedShapes = [...prev];

        if (options?.switchResourceId && ids.length === 1) {
          updatedShapes = prev.map((shape) =>
            shape.type === "spot" && ids.includes(shape.id)
              ? { ...shape, resource_id: undefined }
              : shape
          );
        }

        if (options?.switchIndex && ids.length === 1) {
          const index = prev.findIndex((shape) => shape.id === ids[0]);
          updatedShapes.splice(index, 0, ...newShapes);
          return [...updatedShapes];
        }

        return [...updatedShapes, ...newShapes];
      });

      if (options?.selectDuplicates !== false) {
        setTimeout(() => {
          setSelectedIds(newShapes.map((shape) => shape.id));
        }, 50);
      }

      return newShapes;
    },
    []
  );

  // CMD/CTRL + Arrow shortcut: duplicate spots in direction
  React.useEffect(() => {
    if (selectedIds.length === 0) return;

    const DUPLICATE_MARGIN = 2;

    const handleDuplicateShortcut = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;

      const arrowKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
      if (!arrowKeys.includes(e.key)) return;

      const activeElement = document.activeElement;
      if (
        activeElement &&
        ["INPUT", "TEXTAREA"].includes(activeElement.tagName)
      ) {
        return;
      }

      const currentShapes = shapesRef.current;
      const selectedSpots = currentShapes.filter(
        (s) => selectedIds.includes(s.id) && s.type === "spot"
      );

      if (selectedSpots.length === 0) return;

      e.preventDefault();

      const direction =
        e.key === "ArrowRight"
          ? "right"
          : e.key === "ArrowLeft"
            ? "left"
            : e.key === "ArrowDown"
              ? "bottom"
              : "top";

      // Use visual bounds (getClientRect) for all shapes — gives actual rendered size
      // Transformer is rendered outside the shape Group, so it's not included
      const stage = getWindowKonvaObject().stages[0];
      if (!stage) return;
      const sizes = selectedSpots.map((shape) =>
        getElementSize(shape.id, stage)
      );
      if (!sizes.length) return;
      const gMinX = Math.min(...sizes.map((s) => s?.visualX ?? 0));
      const gMinY = Math.min(...sizes.map((s) => s?.visualY ?? 0));
      const gMaxX = Math.max(
        ...sizes.map((s) => (s?.visualX ?? 0) + (s?.visualWidth ?? 0))
      );
      const gMaxY = Math.max(
        ...sizes.map((s) => (s?.visualY ?? 0) + (s?.visualHeight ?? 0))
      );
      const groupWidth = gMaxX - gMinX;
      let groupHeight = gMaxY - gMinY;

      // When orientation flips top↔bottom, the visual content shifts within the
      // shape group (e.g. top desk: visual y=0–90, bottom desk: visual y=10–100).
      // Correct the offset so the visual gap between old and new shapes is exact.
      // Skip for composites — companions don't move on flip, so visual padding
      // is not symmetric between orientations.
      const hasComposite = selectedSpots.some(
        (s) => s.type === "spot" && (s as SpotShape).companions?.length
      );
      if (!hasComposite && (direction === "bottom" || direction === "top")) {
        const hasVerticalFlip = selectedSpots.some((s) => {
          if (s.type !== "spot") return false;
          const ori = (s as SpotShape).orientation;
          return ori === "top" || ori === "bottom";
        });
        if (hasVerticalFlip) {
          const shapeMinY = Math.min(...selectedSpots.map((s) => s.y));
          const shapeMaxY = Math.max(
            ...selectedSpots.map((s) => s.y + s.height)
          );
          const topPad = gMinY - shapeMinY;
          const bottomPad = shapeMaxY - gMaxY;
          if (direction === "bottom") {
            groupHeight += topPad - bottomPad;
          } else {
            groupHeight += bottomPad - topPad;
          }
        }
      }

      const override: (Partial<Shape> & Required<Pick<Shape, "id">>)[] = [];
      const duplicateIds = selectedSpots.map((shape) => shape.id);

      let lastLabel: string[] | null = mapCanvas.lastSpotLabel || [];

      selectedSpots.forEach((shape) => {
        let x = shape.x;
        let y = shape.y;
        let orientation: SpotOrientation | undefined = (shape as SpotShape)
          .orientation;

        if (direction === "right") {
          x = shape.x + groupWidth + DUPLICATE_MARGIN;
        } else if (direction === "left") {
          x = shape.x - groupWidth - DUPLICATE_MARGIN;
        } else if (direction === "bottom") {
          y = shape.y + groupHeight + DUPLICATE_MARGIN;
        } else if (direction === "top") {
          y = shape.y - groupHeight - DUPLICATE_MARGIN;
        }

        if (direction === "right" || direction === "left") {
          if (orientation === "left") orientation = "right";
          else if (orientation === "right") orientation = "left";
        } else if (direction === "top" || direction === "bottom") {
          if (orientation === "top") orientation = "bottom";
          else if (orientation === "bottom") orientation = "top";
        }

        if (direction === "right" || direction === "bottom") {
          lastLabel = generateNextSpotLabels(
            mapCanvas.spotLabelPattern,
            lastLabel || []
          );
        }

        override.push({
          id: shape.id,
          x,
          y,
          orientation,
          ...(direction === "right" || direction === "bottom"
            ? { label: lastLabel?.join("") || "" }
            : {})
        });
      });

      duplicateShape(duplicateIds, {
        override,
        selectDuplicates: true
      });

      if (direction === "right" || direction === "bottom") {
        mapCanvas.setLastSpotLabel(lastLabel);
      }
    };

    window.addEventListener("keydown", handleDuplicateShortcut);
    return () => window.removeEventListener("keydown", handleDuplicateShortcut);
  }, [selectedIds, duplicateShape, mapCanvas]);

  const toggleOutside = React.useCallback(() => {
    setShowOutside((prev) => !prev);
  }, []);

  const toggleSnapLines = React.useCallback(() => {
    setShowSnapLines((prev) => !prev);
  }, []);

  const toggleGrid = React.useCallback(() => {
    setShowGrid((prev) => !prev);
  }, []);

  const groupSelected = React.useCallback(() => {
    const ids = selectedIdsRef.current;
    if (ids.length < 2) return;

    // Zones are regions of the floor, not furniture: they stay top-level so
    // their coordinates keep meaning something to the server-side sync.
    const selectedShapes = shapesRef.current.filter(
      (shape) => ids.includes(shape.id) && shape.type !== "zone"
    );
    if (selectedShapes.length < 2) return;

    const flattenedShapes = selectedShapes.flatMap((shape) =>
      shape.type === "group" ? flattenGroup(shape) : [shape]
    );

    const bounds = calculateGroupBounds(flattenedShapes);

    const groupShape: GroupShape = {
      id: `group-${Date.now()}`,
      type: "group",
      x: bounds.minX,
      y: bounds.minY,
      width: bounds.maxX - bounds.minX,
      height: bounds.maxY - bounds.minY,
      color: getRandomColor(),
      children: selectedShapes.map((shape) => {
        // biome-ignore lint/suspicious/noExplicitAny: pre-existing shape-clone pattern
        const relative: any = {
          ...shape,
          x: shape.x - bounds.minX,
          y: shape.y - bounds.minY
        };
        if (shape.type === "wall") {
          relative.x2 = (shape as WallShape).x2 - bounds.minX;
          relative.y2 = (shape as WallShape).y2 - bounds.minY;
        }
        return relative;
      }),
      layerType: "shapes"
    };

    const groupedIds = new Set(selectedShapes.map((shape) => shape.id));
    setShapes((prev) => [
      ...prev.filter((shape) => !groupedIds.has(shape.id)),
      groupShape
    ]);
    setSelectedIds([groupShape.id]);
  }, []);

  const ungroupShapes = React.useCallback((groupId: string) => {
    const group = shapesRef.current.find(
      (shape) => shape.id === groupId
    ) as GroupShape;
    if (!group) return;

    const ungroupedShapes = [...group.children];

    // Calculate new positions for ungrouped shapes
    const groupX = group.x;
    const groupY = group.y;
    const newShapes = ungroupedShapes.map((shape) => {
      // biome-ignore lint/suspicious/noExplicitAny: pre-existing shape-clone pattern
      const result: any = {
        ...shape,
        x: shape.x + groupX,
        y: shape.y + groupY
      };
      if (shape.type === "wall") {
        result.x2 = (shape as WallShape).x2 + groupX;
        result.y2 = (shape as WallShape).y2 + groupY;
      }
      return result;
    });

    setShapes((prev) => [
      ...prev.filter((shape) => shape.id !== groupId),
      ...newShapes
    ]);
    setSelectedIds(newShapes.map((shape) => shape.id));

    return newShapes;
  }, []);

  const copySelectedShapes = React.useCallback(():
    | MapEditorClipboard
    | undefined => {
    const ids = selectedIdsRef.current;
    if (ids.length === 0) return;
    const selectedShapes = shapesRef.current.filter((shape) =>
      ids.includes(shape.id)
    );
    const cloned = JSON.parse(JSON.stringify(selectedShapes)) as Shape[];
    setClipboard(cloned);
    // Mirror the same payload to the system clipboard so paste works in
    // other tabs/windows. Fire-and-forget: same-tab paste continues to use
    // the in-memory clipboard above and works even if this fails.
    void writeShapesToSystemClipboard(cloned);

    return { shapes: selectedShapes };
  }, []);

  const pasteShapes = React.useCallback(
    async (
      options?: ClipboardPasteOptions
    ): Promise<MapEditorPasteEvent | undefined> => {
      // Prefer the system clipboard so cross-tab paste works. Fall back to
      // the in-memory clipboard if the system clipboard is empty, blocked,
      // or doesn't carry our sentinel — that's the same path that ran
      // before this change, preserving same-tab behavior.
      const fromSystem = await readShapesFromSystemClipboard();
      const source = fromSystem ?? clipboardRef.current;
      if (source.length === 0) return;

      const offset = 20;

      const duplicateShape = (shape: Shape): Shape => {
        const newShape = {
          ...shape,
          id: generateShapeId(shape.type),
          x: shape.x + offset,
          y: shape.y + offset,
          ...options?.shapeOverrides
        } as Shape;

        if (shape.type === "wall") {
          const wall = shape as WallShape;
          (newShape as WallShape).x2 = wall.x2 + offset;
          (newShape as WallShape).y2 = wall.y2 + offset;
          return newShape;
        }

        if (shape.type === "wall-path") {
          const path = shape as WallPathShape;
          (newShape as WallPathShape).vertices = path.vertices.map((v) => ({
            ...v,
            x: v.x + offset,
            y: v.y + offset
          }));
          return newShape;
        }

        if (shape.type === "spot") {
          return { ...newShape, resource_id: undefined } as Shape;
        }

        if (shape.type === "group" && Array.isArray(shape.children)) {
          return {
            ...newShape,
            children: shape.children.map(duplicateShape) // Recursively duplicate children
          } as Shape;
        }

        return newShape;
      };

      const newShapes = source.map(duplicateShape);

      setShapes((prev) => [...prev, ...newShapes]);
      setSelectedIds(newShapes.map((shape) => shape.id));

      return { newShapes, position: { x: offset, y: offset } };
    },
    []
  );

  const locateShape = React.useCallback((id: string) => {
    const shape = shapesRef.current.find((s) => s.id === id);
    if (!shape) return;

    const stage = getWindowKonvaObject().stages[0];
    if (!stage) return;

    const stageWidth = stage.width();
    const stageHeight = stage.height();

    const shapeCenterX = shape.x + shape.width / 2;
    const shapeCenterY = shape.y + shape.height / 2;

    const newX = stageWidth / 2 - shapeCenterX * stagePositionRef.current.scale;
    const newY =
      stageHeight / 2 - shapeCenterY * stagePositionRef.current.scale;

    setStagePosition((prev) => ({
      ...prev,
      x: newX,
      y: newY
    }));
  }, []);

  const handleDrawToolUpdate = React.useCallback((shape: DrawShape) => {
    setDrawTool((prev) => ({ ...prev, selectedShape: shape }));
  }, []);

  const handleDrawStart = React.useCallback(
    (point: { x: number; y: number }) => {
      setDrawTool((prev) => ({ ...prev, startPoint: point }));
    },
    []
  );

  const handleDrawEnd = React.useCallback(
    (
      start: { x: number; y: number },
      end: { x: number; y: number },
      shape: DrawShape
    ) => {
      const width = Math.abs(end.x - start.x);
      const height = Math.abs(end.y - start.y);
      const x = Math.min(start.x, end.x);
      const y = Math.min(start.y, end.y);

      let newShape: Shape | undefined;
      switch (shape) {
        case "circle": {
          newShape = createCircle({
            x: x + width / 2,
            y: y + height / 2
          }) as CircleShape;
          newShape.width = width;
          newShape.height = height;
          newShape.radius = Math.round(Math.max(width, height) / 2);
          break;
        }
        case "zone": {
          const zone = createZone({ x, y }) as ZoneShape;
          zone.width = width;
          zone.height = height;
          const tag = zoneToolTagRef.current;
          if (tag) {
            zone.zone_id = tag.id;
            zone.categoryTypeId = tag.id;
            zone.name = tag.name;
            mapCanvas.setZoneTypes((prev) =>
              prev.some((entry) => entry.id === tag.id)
                ? prev.map((entry) => (entry.id === tag.id ? tag : entry))
                : [...prev, tag]
            );
          }
          newShape = zone;
          break;
        }
        case "star":
          //newShape = createStar({ x, y }, width, height);
          break;
        default:
          newShape = createRectangle({ x, y });
          newShape.width = width;
          newShape.height = height;
      }

      if (newShape) {
        setShapes((prev) => [...prev, newShape]);
        if (shape === "zone") {
          setSelectedIds([newShape.id]);
        }
      }
      setDrawTool((prev) => ({ ...prev, startPoint: null }));
    },
    [mapCanvas.setZoneTypes]
  );

  const copyNodeStyleToNodeTarget = React.useCallback(
    (source: string, target: string) => {
      const sourceShape = shapesRef.current.find(
        (shape) => shape.id === source
      ) as SpotShape;
      const targetShape = shapesRef.current.find(
        (shape) => shape.id === target
      ) as SpotShape;

      if (!sourceShape || !targetShape) return;

      updateShape(targetShape.id, {
        ...sourceShape,
        id: targetShape.id,
        x: targetShape.x,
        y: targetShape.y,
        textProps: {
          ...sourceShape.textProps,
          text: sourceShape.label
        }
      });
    },
    [updateShape]
  );

  const save = React.useCallback(
    async (options: MapSaveOptions) => {
      const result = await onSaveRef.current?.(
        getCanvasValueRef.current(),
        options
      );
      if (result === false) return false;
      // After a successful save the canvas matches what's on the server, so
      // the Exit confirmation modal and the beforeunload prompt should stop
      // firing until the user makes another change.
      markDirty(false);
      return true;
    },
    [markDirty]
  );

  const handleSetSelectedIds = React.useCallback((selection: string[]) => {
    setHighlightedShape(null);
    setSelectedIds(selection);
  }, []);

  const handleSetEditingResources = React.useCallback(
    (value: ResourceEditingOptions) => {
      setEditingResourcesRef.current(value);
    },
    []
  );

  const clipboardValue = React.useMemo(
    () => ({ shapes: clipboard }),
    [clipboard]
  );

  // Split into 5 focused sub-contexts for granular re-renders

  const actionsValue: MapEditorActionsState = React.useMemo(
    () => ({
      selectedIdsRef,
      addShape,
      addSvg,
      addSvgFromFile,
      addImageFromFile,
      addIconSvg,
      addCustomMultiShape,
      addText,
      updateShape,
      updateShapes,
      duplicateShape,
      deleteShape,
      moveShape,
      setShapes,
      setSelectedIds: handleSetSelectedIds,
      toggleOutside,
      setSnapToGrid,
      toggleSnapLines,
      toggleGrid,
      setShowZoneChecks,
      setShowZones,
      setActiveTool: setActiveToolShowingZones,
      groupSelected,
      ungroupShapes,
      setContextMenuShape,
      locateShape,
      copySelectedShapes,
      pasteShapes,
      undo,
      redo,
      disableHistoryRecording,
      enableHistoryRecording,
      setHighlightedShape,
      setIconToolSelectedIcon,
      setFurnitureToolSelectedFurniture,
      setZoneToolTag,
      handleDrawStart,
      handleDrawEnd,
      setDrawTool: handleDrawToolUpdate,
      setPreviewShape,
      shouldPreventDeselect,
      setEditingResources: handleSetEditingResources,
      setNodeStyleToolTarget,
      copyNodeStyleToNodeTarget,
      save,
      setWallPathSelectedVertexIndex
    }),
    []
  );

  const selectionValue: MapEditorSelectionState = React.useMemo(
    () => ({
      selectedIds
    }),
    [selectedIds]
  );

  const toolValue: MapEditorToolState = React.useMemo(
    () => ({
      activeTool,
      drawTool,
      iconToolSelectedIcon,
      furnitureToolSelectedFurniture,
      zoneToolTag
    }),
    [
      activeTool,
      drawTool,
      iconToolSelectedIcon,
      furnitureToolSelectedFurniture,
      zoneToolTag
    ]
  );

  const uiValue: MapEditorUIState = React.useMemo(
    () => ({
      showOutside,
      snapToGrid,
      showSnapLines,
      showGrid,
      showZoneChecks,
      showZones,
      contextMenuShape,
      highlightedShape,
      previewShape,
      nodeStyleToolTarget,
      canUndo,
      canRedo,
      clipboard: clipboardValue,
      wallPathSelectedVertexIndex,
      isDirty,
      isDirtyRef
    }),
    [
      showOutside,
      snapToGrid,
      showSnapLines,
      showGrid,
      showZoneChecks,
      showZones,
      contextMenuShape,
      highlightedShape,
      previewShape,
      nodeStyleToolTarget,
      canUndo,
      canRedo,
      clipboardValue,
      wallPathSelectedVertexIndex,
      isDirty
    ]
  );

  const propsValue: MapEditorPropsState = React.useMemo(
    () => ({
      resources,
      editingResources,
      onExit,
      title,
      titlePrefix,
      headerSlot,
      zoneEditor,
      restrictedMode,
      saveModes,
      guidePoint
    }),
    [
      resources,
      editingResources,
      onExit,
      title,
      titlePrefix,
      headerSlot,
      zoneEditor,
      restrictedMode,
      saveModes,
      guidePoint
    ]
  );

  // Update editorRef with combined value
  if (typeof editorRef === "function") {
    (editorRef as (value: unknown) => null)({
      ...actionsValue,
      ...selectionValue,
      ...toolValue,
      ...uiValue,
      ...propsValue
    });
  }

  return (
    <MapEditorActionsContext.Provider value={actionsValue}>
      <MapEditorSelectionContext.Provider value={selectionValue}>
        <MapEditorToolContext.Provider value={toolValue}>
          <MapEditorUIContext.Provider value={uiValue}>
            <MapEditorPropsContext.Provider value={propsValue}>
              {children}
            </MapEditorPropsContext.Provider>
          </MapEditorUIContext.Provider>
        </MapEditorToolContext.Provider>
      </MapEditorSelectionContext.Provider>
    </MapEditorActionsContext.Provider>
  );
};
